import React, { useMemo, useState } from 'react';
import { Box, ButtonBase, Chip, Divider, IconButton, Stack, Typography } from '@mui/material';
import {
    ArrowBack as ArrowBackIcon,
    KeyboardArrowUp as CollapseIcon,
    KeyboardArrowDown as ExpandIcon
} from '@mui/icons-material';
import { useNavigate } from 'react-router-dom';
import { PRE_ENTRY_QUESTIONS } from './utils/constants';

const STEP_CONFIG = [
    { number: 1, title: 'Pre-Entry Checklist', path: '/csep/step-1' },
    { number: 2, title: 'Entry Permit', path: '/csep/step-2' }
];

const STEP_STATE_STYLES = {
    complete: {
        label: 'Complete',
        background: '#f6faf6',
        border: '#c5e1c6',
        accent: '#2e7d32',
        circleBg: '#2e7d32',
        circleColor: '#ffffff'
    },
    incomplete: {
        label: 'Incomplete',
        background: '#fff8e1',
        border: '#ffcc80',
        accent: '#f57c00',
        circleBg: '#f57c00',
        circleColor: '#ffffff'
    },
    active: {
        label: 'Current step',
        background: '#eef4fc',
        border: '#b3d4fc',
        accent: '#1565c0',
        circleBg: '#1565c0',
        circleColor: '#ffffff'
    },
    upcoming: {
        label: 'Next',
        background: '#ffffff',
        border: '#d0d7de',
        accent: '#90a4ae',
        circleBg: '#eceff1',
        circleColor: '#546e7a'
    },
    locked: {
        label: 'Locked',
        background: '#fafafa',
        border: '#e0e0e0',
        accent: '#b0bec5',
        circleBg: '#f0f1f2',
        circleColor: '#b0bec5'
    }
};

const STATUS_BADGES = {
    new: { label: 'New', bg: '#e3f2fd', border: '#90caf9', color: '#0d47a1' },
    draft: { label: 'Draft', bg: '#fff3e0', border: '#ffcc80', color: '#e65100' },
    in_progress: { label: 'In Progress', bg: '#fff8e1', border: '#ffe082', color: '#f57c00' },
    completed: { label: 'Completed', bg: '#e8f5e9', border: '#a5d6a7', color: '#2e7d32' },
    submitted: { label: 'Submitted', bg: '#e8f5e9', border: '#a5d6a7', color: '#2e7d32' }
};

export default function CsepStepHeader({
    currentStep,
    csepData,
    disableNavigation = false,
    fromViewer = false
}) {
    const navigate = useNavigate();
    const [navCollapsed, setNavCollapsed] = useState(false);

    const currentStepConfig = useMemo(
        () => STEP_CONFIG.find(step => step.number === currentStep),
        [currentStep]
    );

    const identifier = useMemo(() => {
        const stepOne = csepData?.stepOne || {};
        return stepOne.location || stepOne.jobName || stepOne.jobCode || 'New CSEP';
    }, [csepData]);

    const normalizedStatus = csepData?.status || 'new';
    const statusBadge = STATUS_BADGES[normalizedStatus] || STATUS_BADGES.new;

    const stepStatuses = useMemo(() => {
        const stepOne = csepData?.stepOne || {};
        const stepTwo = csepData?.stepTwo || {};

        // Step 1: Header + Pre-Entry Checklist completion
        const hasLocation = Boolean(stepOne.location || stepOne.jobCode);
        const hasSupervisor = Boolean(stepOne.entrySupervisorName);
        const preEntryAnswers = stepOne.preEntryAnswers || {};
        const answeredCount = Object.values(preEntryAnswers).filter(v => v !== null).length;
        const allAnswered = answeredCount === PRE_ENTRY_QUESTIONS.length;
        const step1Complete = hasLocation && hasSupervisor && allAnswered;

        // Step 2: Entry Permit completion
        const hasDescription = Boolean(stepTwo.project || stepTwo.workDescription);
        const hasSignature = Boolean(stepTwo.entrySupervisorSignature);
        const step2Complete = hasDescription && hasSignature;

        return {
            1: { complete: step1Complete, answeredCount },
            2: { complete: step2Complete }
        };
    }, [csepData]);

    const metaLastStep = csepData?.meta?.lastStep || 1;

    const canAccessStep = (stepNumber) => {
        if (disableNavigation && stepNumber !== currentStep) {
            return false;
        }
        if (fromViewer) {
            return true;
        }
        // Step 2 requires all pre-entry questions answered
        if (stepNumber === 2 && !stepStatuses[1]?.complete) {
            return false;
        }
        if (stepNumber <= metaLastStep) {
            return true;
        }
        return stepNumber === 1;
    };

    const handleStepClick = (stepNumber) => {
        if (stepNumber === currentStep) return;
        if (!canAccessStep(stepNumber)) return;
        const config = STEP_CONFIG.find(step => step.number === stepNumber);
        if (!config) return;
        navigate(config.path, { state: { csep: csepData, isNew: false, viewMode: fromViewer, fromViewer } });
    };

    const handleBack = () => {
        navigate('/csep');
    };

    const renderStepPill = (config) => {
        const status = stepStatuses[config.number] || {};
        const isActive = config.number === currentStep;
        const isComplete = Boolean(status.complete);
        const accessible = canAccessStep(config.number);

        let state = 'upcoming';
        if (!accessible) state = 'locked';
        if (isComplete) state = 'complete';
        if (isActive) state = 'active';

        const stateStyles = STEP_STATE_STYLES[state];
        const helperLabel = state === 'upcoming' ? '' : stateStyles.label;

        // Show progress for step 1
        let progressLabel = null;
        if (config.number === 1 && status.answeredCount !== undefined) {
            progressLabel = `${status.answeredCount}/${PRE_ENTRY_QUESTIONS.length} questions`;
        }

        return (
            <ButtonBase
                key={config.number}
                onClick={() => handleStepClick(config.number)}
                disabled={!accessible}
                sx={{
                    width: '100%',
                    textTransform: 'none',
                    justifyContent: 'flex-start',
                    borderRadius: 1,
                    backgroundColor: stateStyles.background,
                    border: `1px solid ${stateStyles.border}`,
                    color: state === 'locked' ? '#9e9e9e' : '#263238',
                    px: 1.1,
                    py: 0.65,
                    minHeight: 48,
                    display: 'flex',
                    alignItems: 'center',
                    transition: 'all 0.15s ease',
                    '&:hover': {
                        backgroundColor: accessible ? stateStyles.background : undefined,
                        borderColor: accessible ? stateStyles.accent : stateStyles.border
                    },
                    '&.Mui-disabled': {
                        opacity: 1,
                        color: '#9e9e9e',
                        backgroundColor: '#f7f7f7',
                        borderColor: '#e0e0e0'
                    }
                }}
            >
                <Stack direction="row" spacing={1} alignItems="center" sx={{ width: '100%' }}>
                    <Box
                        sx={{
                            width: 26,
                            height: 26,
                            borderRadius: '50%',
                            backgroundColor: stateStyles.circleBg,
                            color: stateStyles.circleColor,
                            fontSize: '0.8rem',
                            fontWeight: 600,
                            display: 'flex',
                            alignItems: 'center',
                            justifyContent: 'center'
                        }}
                    >
                        {config.number}
                    </Box>
                    <Stack spacing={0.25} sx={{ flexGrow: 1 }}>
                        <Typography variant="body2" sx={{ fontWeight: 600, fontSize: { xs: '0.9rem', sm: '0.95rem' } }}>
                            {config.title}
                        </Typography>
                        {(helperLabel || progressLabel) && (
                            <Typography
                                variant="caption"
                                sx={{ fontWeight: 500, color: state === 'locked' ? '#b0bec5' : stateStyles.accent }}
                            >
                                {progressLabel || helperLabel}
                            </Typography>
                        )}
                    </Stack>
                </Stack>
            </ButtonBase>
        );
    };

    const ToggleIcon = navCollapsed ? ExpandIcon : CollapseIcon;

    return (
        <Box
            sx={{
                backgroundColor: '#ffffff',
                borderBottom: '1px solid #e0e0e0',
                px: { xs: 1.25, sm: 1.75 },
                py: { xs: 1.1, sm: 1.3 },
                position: 'sticky',
                top: 0,
                zIndex: 10
            }}
        >
            <Stack
                direction={{ xs: 'column', sm: 'row' }}
                justifyContent="space-between"
                alignItems={{ xs: 'flex-start', sm: 'center' }}
                spacing={{ xs: 0.9, sm: 1.2 }}
            >
                <Stack direction="row" spacing={0.8} alignItems="center">
                    <IconButton
                        onClick={handleBack}
                        size="small"
                        title="Back to CSEP list"
                        sx={{
                            border: '1px solid #d0d7de',
                            borderRadius: 1.5,
                            backgroundColor: 'transparent',
                            width: 36,
                            height: 36,
                            '&:hover': {
                                backgroundColor: '#eef4fc',
                                borderColor: '#90caf9'
                            }
                        }}
                    >
                        <ArrowBackIcon fontSize="small" />
                    </IconButton>
                    <Stack spacing={0.3}>
                        <Typography variant="subtitle2" sx={{ fontWeight: 700, color: '#263238' }}>
                            {identifier}
                        </Typography>
                        <Typography variant="caption" sx={{ color: '#607d8b', letterSpacing: 0.2 }}>
                            Confined Space Entry Permit · Step {currentStep} of 2
                        </Typography>
                    </Stack>
                </Stack>

                <Stack direction="row" spacing={0.6} alignItems="center">
                    <Chip
                        label={statusBadge.label}
                        size="small"
                        variant="outlined"
                        sx={{
                            fontWeight: 600,
                            borderRadius: 2,
                            backgroundColor: '#ffffff',
                            borderColor: statusBadge.border,
                            color: statusBadge.color,
                            height: 28
                        }}
                    />
                    <IconButton
                        size="small"
                        onClick={() => setNavCollapsed(prev => !prev)}
                    >
                        <ToggleIcon fontSize="small" />
                    </IconButton>
                </Stack>
            </Stack>

            {!navCollapsed && (
                <>
                    <Divider sx={{ my: { xs: 0.8, sm: 1 } }} />
                    <Box
                        sx={{
                            display: 'grid',
                            gap: { xs: 0.5, sm: 0.65 },
                            gridTemplateColumns: 'repeat(2, minmax(0, 1fr))'
                        }}
                    >
                        {STEP_CONFIG.map(renderStepPill)}
                    </Box>
                </>
            )}
        </Box>
    );
}
